import { IDropdownSelectOption } from 'components/DropdownSearchSelect/DropdownSearchSelect';
import { ClientApplicationOptions } from 'reduxState/store/application/types';

export interface CreateApplicationOption {
  propertyTarget: keyof ClientApplicationOptions;
  label: string;
}

export const excludedSplitPolicies = new Set([
  'NewPolicyFunction9',
  'SplitPolicyWF100',
  'SplitPolicyWF100',
  'NewPolicyFunction7',
  'SplitPolicyWF35Device65Application0',
  'NewPolicyFunction8',
  'NewPolicyFunction13',
  'NewPolicyFunction12',
  'NewPolicyFunctionEnhancedHalf',
  'NewPolicyFunction14',
]);

export const enhancedSplitPolicies = new Set([
  'SplitPolicyWF35Application65Device0Precision2',
  'NewPolicyFunctionMicrosoftEnhanced',
]);

export const defaultFormData = {
  applicationName: '',
  splitPolicy: null,
  payoutCurrency: null,
  serviceableCountries: null,
  CJPID: '',
  nerfRate: '100',
  productPageUrl: '',
  authType: null,
  partnerUserDecision: null,
  partnerUserReason: '',
};

export const defaultAppOptions = {
  IsAllowList: false,
  DefaultFeaturedMerchants: false,
  MatchProjection: false,
  RequiresITPCompliance: false,
  IsBrowserExtension: false,
  IsCashbackApp: false,
  IsFMTCDisabled: false,
  IsAlwaysRedirect: false,
  RequiresDeviceProfiles: false,
  RequiresDeeplinks: false,
  LoginsSharedWithExtension: false,
  WfEarningsDashboard: false,
};

export const createApplicationOptionItems: CreateApplicationOption[] = [
  {
    propertyTarget: 'IsBrowserExtension',
    label: 'Browser Extension',
  },
  {
    propertyTarget: 'IsCashbackApp',
    label: 'Cashback App',
  },
  {
    propertyTarget: 'IsFMTCDisabled',
    label: 'FMTC Disabled',
  },
  {
    propertyTarget: 'RequiresITPCompliance',
    label: 'Requires ITP Compliance',
  },
  {
    propertyTarget: 'IsAlwaysRedirect',
    label: 'Always Redirect',
  },
  {
    propertyTarget: 'DefaultFeaturedMerchants',
    label: 'Default Featured Merchants',
  },
  {
    propertyTarget: 'RequiresDeviceProfiles',
    label: 'Cloud Device Profiles',
  },
  {
    propertyTarget: 'RequiresDeeplinks',
    label: 'Requires Deeplinks',
  },
  {
    propertyTarget: 'IsAllowList',
    label: 'All Merchants Disabled by Default',
  },
  {
    propertyTarget: 'LoginsSharedWithExtension',
    label: 'Logins will be shared with extension',
  },
  {
    propertyTarget: 'WfEarningsDashboard',
    label: 'WF earnings dashboard/redemption',
  },
];

export const applicationTypeOptions: IDropdownSelectOption[] = [
  {
    label: 'AI',
    value: 'AI',
  },
  {
    label: 'Android SDK',
    value: 'Android SDK',
  },
  {
    label: 'Desktop API',
    value: 'Desktop API',
  },
  {
    label: 'Desktop Chrome Extension',
    value: 'Desktop Chrome Extension',
  },
  {
    label: 'Desktop Chrome Extension Launchpad',
    value: 'Desktop Chrome Extension Launchpad',
  },
  {
    label: 'Desktop Edge Extension',
    value: 'Desktop Edge Extension',
  },
  {
    label: 'Desktop Edge Extension Launchpad',
    value: 'Desktop Edge Extension Launchpad',
  },
  {
    label: 'Desktop Offerwall API',
    value: 'Desktop Offerwall API',
  },
  {
    label: 'Desktop Safari Extension',
    value: 'Desktop Safari Extension',
  },
  {
    label: 'Desktop Safari Extension Launchpad',
    value: 'Desktop Safari Extension Launchpad',
  },
  {
    label: 'Headless Couponator',
    value: 'Headless Couponator',
  },
  {
    label: 'Mobile Android Extension',
    value: 'Mobile Android Extension',
  },
  {
    label: 'Mobile API',
    value: 'Mobile API',
  },
  {
    label: 'Mobile Offerwall API',
    value: 'Mobile Offerwall API',
  },
  {
    label: 'Mobile Safari Extension',
    value: 'Mobile Safari Extension',
  },
  {
    label: 'Mobile Safari Extension Launchpad',
    value: 'Mobile Safari Extension Launchpad',
  },
  {
    label: 'Shopping Portal',
    value: 'Shopping Portal',
  },
];

export const defaultCurrencyOptions: IDropdownSelectOption[] = [
  {
    label: 'USD - United States Dollar',
    value: 'USD',
  },
  {
    label: 'EUR - Euro',
    value: 'EUR',
  },
  {
    label: 'JPY - Japanese Yen',
    value: 'JPY',
  },
  {
    label: 'GBP - British Pound Sterling',
    value: 'GBP',
  },
  {
    label: 'AUD - Australian Dollar',
    value: 'AUD',
  },
  {
    label: 'CAD - Canadian Dollar',
    value: 'CAD',
  },
  {
    label: 'CHF - Swiss Franc',
    value: 'CHF',
  },
  {
    label: 'CNY - Chinese Yuan Renminbi',
    value: 'CNY',
  },
  {
    label: 'SEK - Swedish Krona',
    value: 'SEK',
  },
  {
    label: 'NZD - New Zealand Dollar',
    value: 'NZD',
  },
];

// No default: someone must choose, so nobody turns partner_user on for an app by habit.
// The values are strings because DropdownSearchSelect disables options with a falsy value.
export const PARTNER_USER_ENABLED = 'enabled';
export const PARTNER_USER_EXCLUDED = 'excluded';
export const partnerUserDecisionOptions: IDropdownSelectOption[] = [
  {
    label: 'Yes - this app sends a partner user ID for each user',
    value: PARTNER_USER_ENABLED,
  },
  {
    label: "No - this app doesn't send partner user IDs",
    value: PARTNER_USER_EXCLUDED,
  },
];

// The reason box follows the Yes/No answer so the question and example always fit what was chosen.
export const PARTNER_USER_REASON_NO_ANSWER = 'none';
export const partnerUserReasonText: Record<string, { label: string; placeholder: string }> = {
  [PARTNER_USER_REASON_NO_ANSWER]: {
    label: 'Reason for your answer',
    placeholder: 'Pick Yes or No above first',
  },
  [PARTNER_USER_ENABLED]: {
    label: 'How do you know this app sends partner user IDs?',
    placeholder: 'e.g. Partner confirmed they send their customer ID for each user',
  },
  [PARTNER_USER_EXCLUDED]: {
    label: "Why doesn't this app send partner user IDs?",
    placeholder: 'e.g. WCN client, users share devices',
  },
};

export const authTypeOptions: IDropdownSelectOption[] = [
  {
    label: 'Partner',
    value: 'Partner',
  },
  {
    label: 'Federated',
    value: 'Federated',
  },
];
